import { Component, OnInit, ViewEncapsulation, ViewChild, ElementRef, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { cloneDeep, each, has, forEach, findIndex } from 'lodash';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Enums } from '../../../../../configs/Enums';
import { WORK_SHIFTS_PATHS } from '../../../../work-shifts/work-shifts';
import { WORK_SHIFT_PATHS } from '../../../work-shift';
import { EmployeeStatusesService } from './employee-statuses.service';
import { EmployeeStatusChangeModalComponent } from './modals/employee-status-change-modal/employee-status-change-modal.component';
import { AuthGroupTypesEnum } from '../../../../auth/auth-group-types.enum';
import { AuthService } from '../../../../auth/auth.service';

export interface EmployeeTimeHeader {
  date: Date;
  times: string[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-employee-statuses',
  templateUrl: './employee-statuses.component.html',
  styleUrls: ['./employee-statuses.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class EmployeeStatusesComponent implements OnInit {
  Enums = Enums;
  WORK_SHIFTS_PATHS = WORK_SHIFTS_PATHS;
  moment = require('moment');
  sectorIdOld: number | string;
  sectorId: number | string;
  isFirstInit = true;
  filter = {
    calendar: {
      initDate: null,
      selectedDate: null,
      linkDate: null,
    },
    data: null,
    full_name: '',
    filterType: 'planned',
    currentTime: null,
    isCurrentDay: false,
    onWorkShiftDateChange: this.onWorkShiftDateChange.bind(this),
    onWorkShiftPreviewDateChange: this.onWorkShiftPreviewDateChange.bind(this),
    onWorkShiftNextDateChange: this.onWorkShiftNextDateChange.bind(this),
    onSetCurrentDate: this.onSetCurrentDate.bind(this),
    onSearchUsers: this.onSearchUsers.bind(this),
    onResetName: this.onResetName.bind(this),
    onSetFilter: this.onSetFilter.bind(this),
  };
  params = {
    filter_type: '',
    full_name: '',
    int_begin: '',
  };
  paramsDefault = cloneDeep(this.params);
  statuses = [];
  employers = [];
  times: EmployeeTimeHeader[] = [];
  slidePosition: number;
  countLoadDays: number;
  currentDate: Date;
  countSlideCells: number;
  countCells: number;
  countScrolledCells: number;
  countScrolledCellsForInit: number;
  loading: boolean;
  fixedHeader: boolean;
  noAnimation = true;
  public dateFullFormat = Enums.dates.fullFormat;
  public dateShortFormat = Enums.dates.shortFormat;

  @ViewChild('wrapperHeaderSlide')
  wrapperHeaderSlideElement: ElementRef;
  @ViewChild('monitoringContent')
  monitoringContentElement: ElementRef;
  @ViewChild('wrapperHeaderDaysSlide')
  wrapperHeaderDaysSlideElement: ElementRef;
  @ViewChild('monitoringHeaderWrapper')
  monitoringHeaderWrapperElement: ElementRef;
  @ViewChild('monitoringHeaderFixed')
  monitoringHeaderFixedElement: ElementRef;

  isClickPrevented = null;

  @HostListener('window:scroll.out-zone', []) // notice out-zone
  onWindowScroll() {
    if (!this.monitoringHeaderWrapperElement) {
      return;
    }

    const fixedBlockWidth = this.monitoringHeaderWrapperElement.nativeElement.offsetWidth;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;

    if (window.pageYOffset >= this.monitoringHeaderWrapperElement.nativeElement.getBoundingClientRect().top + scrollTop - 32) {
      if (!this.fixedHeader) {
        this.fixedHeader = true;
        this.monitoringHeaderFixedElement.nativeElement.style.width = fixedBlockWidth + 'px';
        this.monitoringHeaderFixedElement.nativeElement.classList.add('wrapper__header--fixed');
      }
    } else {
      if (this.fixedHeader) {
        this.fixedHeader = false;
        this.monitoringHeaderFixedElement.nativeElement.style.width = 'auto';
        this.monitoringHeaderFixedElement.nativeElement.classList.remove('wrapper__header--fixed');
      }
    }
  }

  constructor(
    private employeeStatusesService: EmployeeStatusesService,
    private router: Router,
    private route: ActivatedRoute,
    public modalService: NgbModal,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.route.parent.params.subscribe((params) => {
      this.sectorIdOld = this.sectorId;
      this.sectorId = params.sector_id;
      this.params = cloneDeep(this.paramsDefault);
      this.filter.full_name = null;
      this.filter.calendar.initDate = null;
      this.filter.calendar.selectedDate = null;
      this.filter.calendar.linkDate = null;
      this.initConstants();

      if (this.isFirstInit) {
        this.isFirstInit = false;
        return;
      }

      if (!this.isFirstInit && this.sectorIdOld && this.sectorIdOld !== this.sectorId) {
        this.initFilter(params);
        let pathArr = this.router.url.split('=');
        this.changeParams('parent', params, pathArr && pathArr.length && pathArr.length > 1);
      }
    });

    this.route.params.subscribe((params: any) => {
      this.noAnimation = true;
      this.initConstants();
      this.initFilter(params);
      this.changeParams('child', params, false);
      this.currentDate = this.moment(this.filter.calendar.initDate).format(this.dateShortFormat);
    });
  }

  get isGRS() {
    return this.authService.isGRSUser();
  }

  initConstants() {
    this.slidePosition = 0;
    this.countLoadDays = this.authService.isGRSUser() ? 3 : 2;
    this.countSlideCells = 1;
    this.countCells = 24;
    this.countScrolledCellsForInit = this.authService.isGRSUser() ? 24 : 7;
    this.fixedHeader = false;
  }

  initFilter(params) {
    let self = this;
    if (has(params, 'int_begin') && params.int_begin) {
      self.filter.calendar.initDate = self.moment(params.int_begin)._d;
      self.filter.calendar.linkDate = self.moment(params.int_begin).format('YYYY-MM');
    }

    if (has(params, 'filter_type') && params.filter_type) {
      self.filter.filterType = params.filter_type;
    } else {
      if (params.full_name) {
        self.filter.filterType = 'all';
        self.params.filter_type = 'all';
      } else {
        self.filter.filterType = 'planned';
        self.params.filter_type = 'planned';
      }
    }

    if (has(params, 'full_name') && params.full_name) {
      self.filter.full_name = params.full_name;
    }

    if (!self.filter.calendar.initDate) {
      self.filter.calendar.initDate = this.isGRS ? self.moment().utcOffset(0).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toDate() : new Date();
      self.filter.calendar.linkDate = self.moment(self.filter.calendar.initDate).format('YYYY-MM');
    }
  }

  changeParams(type, params, isUpdateAfterChangeParent) {
    if (type === 'parent' && isUpdateAfterChangeParent) {
      console.log('~~~~ PARENT UPDATE ~~~~');
      this.getEmployers();
      return;
    }

    if (type === 'child') {
      let countParameters = 0;
      let countExistsParameters = 0;

      each(this.params, function (value, key) {
        countParameters++;

        if (has(params, [key])) {
          countExistsParameters++;
        }
      });

      forEach(params, (value, key) => {
        this.params[key] = value;
      });

      if (countParameters === countExistsParameters) {
        console.log('~~~~ CHILD UPDATE ~~~~');
        this.getEmployers();
      } else {
        console.log('navigate with replace');
        this.params.int_begin = this.moment(this.filter.calendar.initDate).format(this.dateFullFormat);
        this.router.navigate([WORK_SHIFT_PATHS.work_shift, WORK_SHIFT_PATHS.work_shift_s, this.sectorId, WORK_SHIFT_PATHS.work_shift_worker_statuses, this.params], {
          replaceUrl: true,
        });
      }
    }
  }

  getEmployers() {
    this.makeTimes();

    this.loading = true;

    const { intBegin, intEnd } = this.getRange();

    this.employeeStatusesService
      .getEmployeeDataUnion({
        int_begin: this.moment(intBegin).format(this.dateShortFormat),
        int_end: this.moment(intEnd).format(this.dateShortFormat),
        sector_id: this.sectorId,
        name: this.params.full_name,
        filter_type: this.params.filter_type,
        groups: this.isGRS ? [AuthGroupTypesEnum.ServiceEngineerGRS] : [AuthGroupTypesEnum.ServiceEngineer],
      })
      .subscribe(
        (response) => {
          this.employers = response[0]['data'];
          this.filter.data = response[1]['data'];
          this.preventClicks(null);
          this.getWorkersStatuses();
        },
        () => {
          this.employers = [];
          this.loading = false;
        }
      );
  }

  checkCurrentDate() {
    let initDateMillis = this.moment(this.filter.calendar.initDate).format(this.dateShortFormat);
    let currentDateMillis = this.moment(this.filter.currentTime).format(this.dateShortFormat);

    this.filter.isCurrentDay = initDateMillis === currentDateMillis;
  }

  getRange() {
    const intBegin = this.moment(this.filter.calendar.initDate).startOf('day');

    const formattedIntBegin = this.isGRS ? intBegin.clone().subtract(1, 'day').toDate() : intBegin.toDate();

    const intEnd = this.isGRS
      ? intBegin.clone().add(this.countLoadDays, 'day').toDate()
      : intBegin
          .clone()
          .add(this.countLoadDays - 1, 'day')
          .toDate();

    const diff = this.moment(intEnd).diff(intBegin, 'day');
    const range = new Array(diff).fill(0).map((_, index) => this.moment(intBegin).add(index, 'day').format(this.dateShortFormat));

    return {
      intBegin: formattedIntBegin,
      intEnd: intEnd,
      range,
    };
  }

  getWorkersStatuses() {
    const { intBegin, intEnd } = this.getRange();

    this.noAnimation = true;
    this.employeeStatusesService
      .getEmployersStatuses({
        int_begin: this.moment(intBegin).format(this.dateShortFormat),
        int_end: this.moment(intEnd).format(this.dateShortFormat),
        sector_id: this.sectorId,
        user_ids: this.employers.map((w) => w.u_id),
      })
      .subscribe(
        (response) => {
          this.statuses = response['data'].intervals;
          this.filter.currentTime = response['data'].current_time;
          this.checkCurrentDate();
          this.assignDataToEmployer();
          this.loading = false;
          this.scrollToPosition(this.countScrolledCellsForInit);
        },
        () => {
          this.statuses = null;
          this.assignDataToEmployer();
          this.loading = false;
        }
      );
  }

  trackByFn(index, item) {
    return item.workday;
  }

  trackByIndex(index) {
    return index;
  }

  makeTimes() {
    this.times = [];

    const { intBegin } = this.getRange();

    for (let i = 0; i < this.countLoadDays; ++i) {
      const date = this.moment(intBegin).add(i, 'day').startOf('day').format(this.dateShortFormat);

      const t = Array.from({ length: 24 }, (_, k) => `${k < 10 ? '0' : ''}${k}.00`);

      this.times.push({ date, times: t });
    }
  }

  assignDataToEmployer() {
    forEach(this.employers, (employee) => {
      let i = findIndex(this.statuses, { user_id: employee.u_id });

      if (i > -1) {
        employee.statuses = this.statuses[i].intervals;
        employee.worklog = this.statuses[i].worklog;
      }

      this.makeCurrentDay(employee);
      this.groupByDay(employee);
    });
  }

  makeCurrentDay(employee) {
    employee.currentWorkDay = {
      isWorked: false,
      isWorkedOtherSector: false,
      cellType: null,
      sector: null,
      int_begin: '',
      int_end: '',
    };
    if (has(employee, 'working_shift') && employee.working_shift.length) {
      let currentDate = this.moment(this.filter.calendar.initDate).format(this.dateShortFormat);
      let indexWorkDay = findIndex(employee.working_shift, {
        workday: currentDate,
      });

      if (indexWorkDay > -1) {
        let schedule = employee.working_shift[indexWorkDay].schedule;

        employee.currentWorkDay.sector = (schedule && schedule.sector) || null;
        employee.currentWorkDay.cellType = (schedule && schedule.cell_type) || null;
        employee.currentWorkDay.isWorked = !!((schedule && schedule.cell_type) || schedule.sector);

        // Для формирования ссылки на страницу графика нужен sector_id, в случае командировки берем из
        // гостевого участка
        if (has(schedule, 'sector_id') && schedule.sector_id) {
          employee.sector_id = schedule.sector_id;

          if (schedule.sector_id !== Number(this.sectorId)) {
            employee.currentWorkDay.isWorkedOtherSector = true;
          }
        }

        if ((has(schedule, 'sector_id') && !schedule.sector_id) || schedule.sector_id === Number(this.sectorId)) {
          if (schedule.int_begin && schedule.int_end) {
            employee.currentWorkDay.int_begin = (schedule && schedule.int_begin) || null;
            employee.currentWorkDay.int_end = (schedule && schedule.int_end) || null;
          }
        }
      }
    }
  }

  groupByDay = (employee) => {
    let groupStatusesDate = {};

    employee.groupByDays = { days: [], worked: {} };
    const { intBegin } = this.getRange();

    // группировка статусов по датам, так как приходят не сгруппированные в одном массиве
    forEach(employee.statuses, (s) => {
      let intDate = s.int_begin.slice(0, 10);
      let currentDate = this.moment(this.currentDate).format(this.dateShortFormat);

      if (!employee.groupByDays.worked[s.int_type] && s.int_type) {
        employee.groupByDays.worked[s.int_type] = [];
      }
      if (!groupStatusesDate[intDate]) {
        groupStatusesDate[intDate] = [];
      }

      if (intDate === currentDate && s.int_type) {
        employee.groupByDays.worked[s.int_type].push(s);
      }

      // Этим условием отсекаем статусы с int_type = 0, а также исключительную ситуацию когда int_begin = int_end.
      if (s.int_type && s.int_begin !== s.int_end) {
        groupStatusesDate[intDate].push(s);
      }
    });

    // собираем данные по дням(рабочий интервал, ячейки, статусы)
    for (let i = 0; i < this.countLoadDays; ++i) {
      let date = this.moment(intBegin).add(i, 'days').format(this.dateShortFormat);
      let indexWorkDay = findIndex(employee.working_shift, {
          workday: date,
        }),
        day = {
          workday: date,
          times: Array.apply(0, new Array(24)).map((k, index) => index),
          workInterval: null,
          workStatuses: groupStatusesDate[date] || null,
        };

      if (indexWorkDay > -1) {
        let schedule = employee.working_shift[indexWorkDay]['schedule'] || null;

        if ((has(schedule, 'sector_id') && !schedule.sector_id) || schedule.sector_id === Number(this.sectorId)) {
          if (schedule.int_begin && schedule.int_end) {
            day.workInterval = {
              int_begin: schedule.int_begin || null,
              int_end: schedule.int_end || null,
            };
          } else {
            day.workInterval = null;
          }
        }
      }

      employee.groupByDays.days.push(day);
    }
  };

  scrollToPosition(countCells) {
    this.noAnimation = true;
    setTimeout(() => {
      this.slidePosition -= (this.getTimeWrapperWidth() / (this.countLoadDays * this.countCells)) * countCells;
      this.slide();
      this.updateCurrentDate();
    }, 350);
  }

  onWorkShiftDateChange(event) {
    if (event && event.value) {
      this.filter.calendar.selectedDate = event.value;
      this.currentDate = this.moment(this.filter.calendar.selectedDate).format(Enums.dates.dateFormat);
      this.params.int_begin = this.moment(this.filter.calendar.initDate).format(this.dateFullFormat);
      this.navigate();
      this.resetPosition();
    }
  }

  onWorkShiftPreviewDateChange() {
    const date = this.moment(this.currentDate).startOf('day').add(-1, 'day').format('YYYY-MM-DDTHH:mm');
    this.filter.calendar.initDate = new Date(date);

    this.params.int_begin = this.moment(this.filter.calendar.initDate).format(this.dateFullFormat);
    this.navigate();
    this.resetPosition();
  }

  onWorkShiftNextDateChange() {
    const date = this.moment(this.currentDate).startOf('day').add(1, 'day').format('YYYY-MM-DDTHH:mm');
    this.filter.calendar.initDate = new Date(date);
    this.currentDate = this.moment(this.filter.calendar.initDate).format(Enums.dates.dateFormat);
    this.params.int_begin = this.moment(this.filter.calendar.initDate).format(this.dateFullFormat);
    this.navigate();
    this.resetPosition();
  }

  onSetCurrentDate() {
    this.filter.calendar.initDate = new Date();
    this.currentDate = this.moment(this.filter.calendar.initDate).format(Enums.dates.dateFormat);
    this.params.int_begin = this.moment(this.filter.calendar.initDate).format(this.dateFullFormat);
    this.navigate();
  }

  onOpenChangeStatusDialog(employee) {
    const employeeChangeStatusDialog = this.modalService.open(EmployeeStatusChangeModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    employeeChangeStatusDialog.componentInstance.employee = employee;
    employeeChangeStatusDialog.componentInstance.sectorId = this.sectorId;
  }

  resetPosition() {
    this.noAnimation = true;
    this.countScrolledCells = 0;
    this.slidePosition = 0;
    this.countLoadDays = this.authService.isGRSUser() ? 3 : 2;
    this.countSlideCells = 1;
    this.countCells = 24;
    this.fixedHeader = false;
  }

  navigate() {
    this.router.navigate([WORK_SHIFT_PATHS.work_shift, WORK_SHIFT_PATHS.work_shift_s, this.sectorId, WORK_SHIFT_PATHS.work_shift_worker_statuses, this.params]);
  }

  getTimeWrapperWidth() {
    const contentTimeBlockWrapperEl = this.monitoringContentElement && this.monitoringContentElement.nativeElement.querySelector('.content-time-block__wrapper');

    return contentTimeBlockWrapperEl ? contentTimeBlockWrapperEl.offsetWidth : 0;
  }

  getCountTimeWidth() {
    return (this.getTimeWrapperWidth() / (this.countLoadDays * this.countCells)) * this.countCells;
  }

  getCountSlide() {
    return (this.getTimeWrapperWidth() / (this.countLoadDays * this.countCells)) * this.countSlideCells;
  }

  getTimelineWidth() {
    return (document.querySelector('.timeline') as HTMLElement).offsetWidth;
  }

  updateCurrentDate() {
    const pxPerDay = this.getCountSlide() * 24;
    if (!pxPerDay) {
      return;
    }

    const dayIndex = Math.floor(Math.abs(this.slidePosition) / pxPerDay);

    if (dayIndex >= 0 && dayIndex < this.times.length) {
      this.currentDate = this.times[dayIndex].date;
    }
    const isSame = this.moment(this.currentDate).isSame(this.times[dayIndex].date, 'day');

    if (!isSame) {
      this.employers.forEach(this.groupByDay);
    }
  }

  preventClicks(state = true) {
    this.isClickPrevented = state;
  }

  onPrevSlide() {
    if (this.slidePosition >= 0) {
      this.preventClicks();
      this.onWorkShiftPreviewDateChange();
      return;
    }

    this.slidePosition += this.isGRS && Math.abs(this.slidePosition) === this.getCountSlide() * 24 ? this.getCountSlide() * 24 : this.getCountSlide();

    this.noAnimation = false;
    setTimeout(() => {
      this.slide();
      this.updateCurrentDate();
    });
  }

  onNextSlide() {
    this.slidePosition -= this.getCountSlide();

    if (this.getTimeWrapperWidth() + this.slidePosition < this.getCountTimeWidth()) {
      this.preventClicks();
      return this.onWorkShiftNextDateChange();
    }

    if (this.slidePosition - -(this.getTimeWrapperWidth() / this.countLoadDays) === -this.getCountTimeWidth()) {
      return;
    }

    this.noAnimation = false;
    setTimeout(() => {
      this.slide();
      this.updateCurrentDate();
    });
  }

  onSearchUsers() {
    this.params.full_name = (this.filter && this.filter.full_name) || '';
    this.params.filter_type = 'all';
    this.filter.filterType = 'all';

    this.navigate();
  }

  onResetName() {
    this.params.full_name = '';
    this.filter.full_name = '';
    this.params.filter_type = 'planned';
    this.filter.filterType = 'planned';

    this.navigate();
  }

  onSetFilter(type) {
    this.filter.filterType = type;
    this.params.filter_type = type;

    this.navigate();
  }

  slide() {
    let elements = this.monitoringContentElement.nativeElement.querySelectorAll('.content-time-block__wrapper');

    this.wrapperHeaderSlideElement.nativeElement.style.left = this.slidePosition + 'px';
    this.wrapperHeaderDaysSlideElement.nativeElement.style.left = this.slidePosition + 'px';
    [].forEach.call(elements, (elem) => {
      elem.style.left = this.slidePosition + 'px';
    });
  }
}
